import { AWARD_PRIZE_TYPE, PrizeSource } from 'lib/constants/award';
import { getUserPrizeList, getUnusedFragmentList, getUsedFragmentList } from 'lib/apis/nodeApi';
import monitor from 'lib/utils/monitor';
import toast from 'lib/utils/toast';
import { COMMON_MSG } from 'lib/constants/error';
import { create } from 'zustand';

interface UserRewardStore {
  actId: number;
  offset: number; // 碎片消耗记录偏移量
  createTime: string; // 碎片消耗记录创建时间 后台按照碎片时间分页 这个值要维护
  lotteryActIdList: any; // 抽奖活动id列表
  usedFragmentList: Array<Record<string, any>>; // 碎片奖励
  unusedFragmentList: Array<Record<string, any>>; // 碎片奖励
  commonPrizeResult: Array<Record<string, any>>; // 非碎片奖励（合成奖励+非碎片奖励）
  setLotteryActIdList: any; // 抽奖活动id赋值
  setUsedFragmentList: (unusedFragmentList: Array<Record<string, any>>) => void; // 已消耗碎片列表
  setUnusedFragmentList: (unusedFragmentList: Array<Record<string, any>>) => void; // 碎片奖励赋值
  setCommonLotteryPrizeList: (commonLotteryPrizeList: Array<Record<string, any>>) => void; // 非碎片奖励赋值
  queryCommonPrizeResult: (mobile: string, skipErrorToast?: boolean) => any; // 请求非碎片奖励（node）
  queryFragmentPrizeResult: () => void; // 请求未使用碎片奖励（node）
  queryUsedFragmentPrizeResult: ({ limit }) => void; // 请求已消耗或过期碎片奖励（node）
  appendLotteryActIdList: (actIds: Array<string>) => void; // 抽奖活动id追加
}

// 排序 先外链，然后合成奖励，最后是其他奖励
function customSort(a, b) {
  const aIsLink = a.prizeType === AWARD_PRIZE_TYPE.OUTER_LINK;
  const bIsLink = b.prizeType === AWARD_PRIZE_TYPE.OUTER_LINK;
  const aIsSynthesis = a.prizeSource === PrizeSource.FRAGMENT_FUSION;
  const bIsSynthesis = b.prizeSource === PrizeSource.FRAGMENT_FUSION;

  if (aIsLink && !bIsLink) {
    return -1; // a是外链奖励，排在b前面
  }
  if (!aIsLink && bIsLink) {
    return 1; // b是外链奖励，排在a前面
  }
  if (aIsSynthesis && !bIsSynthesis) {
    return -1; // a是合成奖励，排在b前面
  }
  if (!aIsSynthesis && bIsSynthesis) {
    return 1; // b是合成奖励，排在a前面
  }
  return 0; // a和b类型相同，保持原有顺序
}

//  排序碎片
function sortFragmentPrizeList(prizeList: Array<Record<string, any>>) {
  // 抽奖接口和抽奖记录接口返回的顺序不一致，所以先按id排序，然后把外链奖品排在前面,两个排序分开更好理解
  return prizeList.sort((a, b) => a.id - b.id);
}
export const useUserRewardStore = create<UserRewardStore>((set, get) => ({
  actId: 0,
  offset: 0, // 碎片消耗记录偏移量
  lotteryActIdList: [],
  commonPrizeResult: [],
  unusedFragmentList: [],
  usedFragmentList: [],
  createTime: '',
  setCommonLotteryPrizeList: (commonPrizeResult) => {
    set((state) => ({
      ...state,
      commonPrizeResult,
    }));
  },
  setLotteryActIdList: (actId) => {
    // 删除为空的actId
    if (!actId) {
      return;
    }
    set((state) => {
      // 检查actId是否已经存在于lotteryActIdList中
      const isDuplicate = state.lotteryActIdList.includes(actId);
      if (isDuplicate || !actId) {
        return state;
      }
      return {
        ...state,
        lotteryActIdList: [...state.lotteryActIdList, actId],
      };
    });
  },
  appendLotteryActIdList: (actIds) => {
    // 需要去重
    const finalActIds = Array.from(new Set([...get().lotteryActIdList, ...actIds]));
    set((state) => ({
      ...state,
      lotteryActIdList: finalActIds,
    }));
  },
  setFragmentPrizeDetail: (fragmentPrizeDetail) => {
    set((state) => ({
      ...state,
      fragmentPrizeDetail,
    }));
  },
  setUnusedFragmentList: (unusedFragmentList) => {
    set((state) => ({
      ...state,
      unusedFragmentList,
    }));
  },
  setUsedFragmentList: (usedFragmentList) => {
    set((state) => ({
      ...state,
      usedFragmentList,
    }));
  },
  queryCommonPrizeResult: async (mobile, skipErrorToast) => {
    const actIdList = get().lotteryActIdList;
    toast.loading();
    const res = await getUserPrizeList({
      actIdList,
      mobile,
    });
    toast.close();
    const handleErr = () => {
      toast.info(COMMON_MSG);
      set((state) => ({
        ...state,
        commonPrizeResult: [],
      }));
      return;
    };

    if (res?.code !== 0) {
      monitor.reportLog({
        actionName: 'common userPrize result query failed',
      });
      if (skipErrorToast) return;
      handleErr();
      return;
    }

    if (res?.data?.code && res?.data?.code !== 0) {
      monitor.reportEvent('fusionFragmentsError');
      if (!skipErrorToast) {
        toast.info('有碎片奖励合成失败，请联系福利鹅公众号客服查询～');
      }
    }
    const commonPrizeResult = (res?.data?.drawResult || []).sort(customSort);
    set((state) => ({
      ...state,
      commonPrizeResult,
    }));
    return commonPrizeResult;
  },
  queryFragmentPrizeResult: async () => {
    toast.loading();
    const res = await getUnusedFragmentList();
    toast.close();

    const handleErr = () => {
      toast.info(COMMON_MSG);
      set((state) => ({
        ...state,
        unusedFragmentList: [],
      }));
      return;
    };
    if (res?.code !== 0) {
      monitor.reportLog({
        actionName: 'fragment userPrize result query failed',
      });
      handleErr();
      return;
    }

    set((state) => ({
      ...state,
      unusedFragmentList: sortFragmentPrizeList(res.data?.fragmentList || []),
    }));

    return;
  },
  queryUsedFragmentPrizeResult: async ({ limit }) => {
    const { offset, createTime } = get();
    const res = await getUsedFragmentList({
      offset,
      limit,
      createTime,
    });

    const handleErr = () => {
      toast.info(COMMON_MSG);
      set((state) => ({
        ...state,
        commonPrizeResult: [],
      }));
      return;
    };

    if (res?.code !== 0) {
      monitor.reportLog({
        actionName: 'fragment userPrize result query failed',
      });
      handleErr();
      return;
    }

    set((state) => ({
      ...state,
      usedFragmentList: [...(res.data?.fragmentList || [])],
      offset: 0,
      createTime: '',
    }));
    return;
  },
}));
